import { Component, ViewChild } from '@angular/core';
import { NgbDate, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { ColumnData } from '../../shared/models/column-data.model';
import { ColumnDataService } from '../../shared/services/column-data.service';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { JhiLanguageService } from '../../../shared/core-utils';
import { StudyEditorContext } from '../../study-editor.context';
import { GermplasmDetailsPopupService } from '../../../germplasm-details/germplasm-details-popup.service';
import { Principal } from '../../../shared';
import { VIEW_GERMPLASM_DETAILS_PERMISSION, VIEW_NAMES_PERMISSIONS } from '../../../shared/auth/permissions';
import { SampleGenotypeModel } from '../../../entities/sample/genotype/sample-genotype.model';
import { SampleGenotypeService } from '../../../entities/sample/genotype/sample-genotype.service';
import { SampleListService } from '../../../entities/sample/sample-list.service';
import { StudySampleList } from '../../../entities/sample/sample-list.model';
import { GenotypeFilter } from '../../../entities/sample/genotype/genotype-filter.model';
import { SampleGenotypeSearchRequestDTO } from '../../../entities/sample/genotype/sample-genotype-search-request.model';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { DateHelperService } from '../../../shared/service/date.helper.service';
import { MAX_NAME_DISPLAY_SIZE } from '../../../app.constants';
import {
    getStaticColumnLabelKey,
    STATIC_COLUMN_DESIGNATION,
    STATIC_COLUMN_ENTRY_NO,
    STATIC_COLUMN_ENTRY_TYPE, STATIC_COLUMN_GID,
    STATIC_COLUMN_OBS_UNIT_ID, STATIC_COLUMN_PLOT_NO,
    STATIC_COLUMN_SAMPLE_NAME,
    STATIC_COLUMN_SAMPLE_UUID,
    STATIC_COLUMN_SAMPLING_DATE,
    STATIC_COLUMN_SAMPLING_TAKEN_BY, STATIC_COLUMN_TRIAL_INSTANCE
} from '../../../shared/util/static-columns-util';

@Component({
    selector: 'jhi-sample-genotype',
    templateUrl: './sample-genotype.component.html',
    styleUrl: './sample-genotype.component.scss'
})
export class SampleGenotypeComponent {

    public readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    public readonly STATIC_COLUMN_TRIAL_INSTANCE = STATIC_COLUMN_TRIAL_INSTANCE;
    public readonly STATIC_COLUMN_GID = STATIC_COLUMN_GID;
    public readonly STATIC_COLUMN_DESIGNATION = STATIC_COLUMN_DESIGNATION;
    public readonly STATIC_COLUMN_ENTRY_NO = STATIC_COLUMN_ENTRY_NO;
    public readonly STATIC_COLUMN_ENTRY_TYPE = STATIC_COLUMN_ENTRY_TYPE;
    public readonly STATIC_COLUMN_PLOT_NO = STATIC_COLUMN_PLOT_NO;
    public readonly STATIC_COLUMN_OBS_UNIT_ID = STATIC_COLUMN_OBS_UNIT_ID;
    public readonly STATIC_COLUMN_SAMPLE_NAME = STATIC_COLUMN_SAMPLE_NAME;
    public readonly STATIC_COLUMN_SAMPLE_UUID = STATIC_COLUMN_SAMPLE_UUID;
    public readonly STATIC_COLUMN_SAMPLING_DATE = STATIC_COLUMN_SAMPLING_DATE;
    public readonly STATIC_COLUMN_SAMPLING_TAKEN_BY = STATIC_COLUMN_SAMPLING_TAKEN_BY;

    readonly ALL_SAMPLE_LIST_ID = -1;

    @ViewChild('p', { static: false }) columnFilterPopover: NgbPopover;

    displayedColumns: ColumnData[] = [];
    entries: SampleGenotypeModel[] = [];
    sampleLists: StudySampleList[];
    selectedSampleList: StudySampleList;

    numberOfEntries = 0;
    numberOfFilteredEntries: number;
    currentPage = 1;
    pageSize = 50;
    isReloadPending: boolean;
    reloadTimeout: any;
    isTableDataReloadPending: boolean;
    tableDataReloadTimeout: any;
    reloadDelay: number = 800;
    isCategoricalDescriptionView = false;

    activePopover?: NgbPopover;
    columnDataForFilter: ColumnData;
    columnDataService: ColumnDataService;
    isSelectAll = false;

    hasViewGermplasmDetailsPermission = true;
    hasViewNamesPermission = true;

    subscriptionManager = new SubscriptionsManager();

    constructor(private jhiLanguageService: JhiLanguageService,
                private studyEditorContext: StudyEditorContext,
                public germplasmDetailsPopupService: GermplasmDetailsPopupService,
                public principal: Principal,
                private sampleGenotypeService: SampleGenotypeService,
                private sampleListService: SampleListService,
                private translateService: TranslateService,
                private dateHelperService: DateHelperService) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    async ngOnInit() {

        this.columnDataService = new ColumnDataService(ColumnDataService.SAMPLE_GENOTYPES_KEY, String(this.studyEditorContext.studyId));

        this.hasViewGermplasmDetailsPermission = this.principal.hasAnyAuthorityDirect(VIEW_GERMPLASM_DETAILS_PERMISSION);
        this.hasViewNamesPermission = this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSIONS);

        const sampleListResponse = await this.sampleListService.getSampleListsInStudy(this.studyEditorContext.studyId, true).toPromise();
        this.sampleLists = sampleListResponse.body;
        const allSampleList = new StudySampleList();
        allSampleList.listId = this.ALL_SAMPLE_LIST_ID;
        allSampleList.listName = this.translateService.instant('study-editor.sample-genotype.select-sample-list.all-items');
        this.sampleLists.unshift(allSampleList);
        this.selectedSampleList = this.sampleLists[0];

        this.loadTable();
    }

    /**
     * Efficiently reload table
     */
    scheduleReload(): void {
        if (this.isReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.reloadTimeout);
        } else {
            this.isReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.reloadTimeout = setTimeout(() => {
            this.loadTable();
            this.isReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    /**
     * Efficiently reload table data
     */
    scheduleTableDataReload(pageIndex, pageSize): void {
        if (this.isTableDataReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.tableDataReloadTimeout);
        } else {
            this.isTableDataReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.tableDataReloadTimeout = setTimeout(() => {
            this.loadTableData(pageIndex, pageSize);
            this.isTableDataReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    loadTable(): void {
        this.loadTableHeader().then(() => {
            this.loadTableData();
        });
    }

    getSampleListIds() {
        if (this.selectedSampleList.listId === this.ALL_SAMPLE_LIST_ID) {
            return this.sampleLists.filter(sampleList => sampleList.listId !== this.ALL_SAMPLE_LIST_ID).map((sampleList) => sampleList.listId);
        } else {
            return [this.selectedSampleList.listId];
        }
    }

    async loadTableHeader() {

        const columnsResponse = await this.sampleGenotypeService.getColumns(this.studyEditorContext.studyId, this.getSampleListIds()).toPromise();
        if (columnsResponse) {
            this.displayedColumns = this.columnDataService.displayedColumns = columnsResponse as ColumnData[];
            this.displayedColumns.forEach(column => {
                column.selected = true;
                column.visible = true;
                column.columnSelectorVisible = true;
                column.displayName = column.name;
            });
            this.displayedColumns = this.columnDataService.applyColumnsState();

            const designationColumn = this.displayedColumns.find((column) => column.termId === this.STATIC_COLUMN_DESIGNATION);
            if (designationColumn) {
                designationColumn.visible = this.hasViewNamesPermission;
                designationColumn.selected = this.hasViewNamesPermission;
                designationColumn.columnSelectorVisible = this.hasViewNamesPermission;
            }
        }
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.scheduleTableDataReload((page - 1), this.pageSize);
    }

    onPageSizeChange($event: Event) {
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    loadTableData(pageIndex = 0, pageSize = 50): void {
        const filter = this.getFilter();
        const sort = this.getSort();
        const searchData: SampleGenotypeSearchRequestDTO = {
            studyId: this.studyEditorContext.studyDetails.id,
            filter: filter
        };

        this.sampleGenotypeService.searchSampleGenotypes(this.studyEditorContext.studyDetails.id, searchData, pageIndex, this.pageSize, sort).toPromise()
            .then((paginatedResponse) => {
                this.entries = paginatedResponse.data;
                this.numberOfEntries = paginatedResponse.totalCount;
                this.numberOfFilteredEntries = paginatedResponse.filteredCount;
            });
    }

    trackRowsById(entry: SampleGenotypeModel): string {
        return entry.sampleUUID;
    }

    getSort(): string {

        const sort = this.displayedColumns.reduce((sort, column) => {
            if (column.sortingAsc !== null && column.sortingAsc !== undefined) {
                return column.name + ',' + (column.sortingAsc ? 'asc' : 'desc');
            }
            return sort;
        }, '');

        return sort;
    }

    getFilter(): GenotypeFilter {

        const filter: GenotypeFilter = {
            sampleListIds: this.getSampleListIds(),
            filteredValues: this.displayedColumns.reduce((map, column) => {
                column.isFiltered = false;

                if (this.isTextFilter(column)) {
                    return map;
                }

                if (column.possibleValues) {
                    column.possibleValues.forEach((value)=> {
                        if (value.isSelectedInFilters) {
                            if (!map[column.termId]) {
                                map[column.termId] = [];
                            }
                            map[column.termId].push(value.name);
                        }
                    });
                    if (!map[column.termId] && column.query) {
                        map[column.termId] = [column.query];
                    }
                } else if (column.query) {
                    map[column.termId] = [(column.query)];
                }

                if (map[column.termId]) {
                    column.isFiltered = true;
                }
                return map;
            }, {} as Map<string, string[]>),
            filteredTextValues: this.displayedColumns.reduce((map, column)=>  {
                if (!this.isTextFilter(column)) {
                    return map;
                }
                if (column.query) {
                    if (column.dataType === 'Date') {
                        map[column.termId] = this.dateHelperService.convertFormattedNgbDateToString(column.query as unknown as NgbDate, '-');
                    } else {
                        map[column.termId] = column.query;
                    }
                    column.isFiltered = true;
                }
                return map;
            }, {} as Map<string, string>),
            variableTypeMap: this.displayedColumns.reduce((map, column)=> {
                map[column.termId] = column.variableType;
                return map;
            }, {} as Map<string, string>)
        };

        return filter;
    }

    trackColumnsById(column: ColumnData) {
        return column.termId;
    }

    // Filters

    showColumnFilterPopover(column: ColumnData, p: NgbPopover) {
        this.columnDataForFilter = column;
        p.open({
            column: column
        });
    }

    sortColumn(column: ColumnData, ascending: boolean): void {

        // Clear sorting state for all displayed columns to ensure only one column can be sorted at a time
        this.resetSortingsOfDisplayedColumns();

        column.sortingAsc = ascending;
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    filterByColumn(column: ColumnData) {
        column.isFiltered = true;
        this.columnFilterPopover.close();
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    resetFilterByColumn(column: ColumnData) {
        column.isFiltered = false;
        column.query = '';
        column.sortingAsc = null;
        column.possibleValues?.forEach(value => {
            value.isSelectedInFilters = false;
        });
        column.isSelectAll = false;
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    resetSortingsOfDisplayedColumns() {
        this.displayedColumns.forEach(column => {
            column.sortingAsc = null;
        });
    }

    filterSelectAll(column: ColumnData, isSelectAll: boolean) {
        if (column.possibleValues) {
            column.possibleValues.forEach(value => {
                value.isSelectedInFilters = isSelectAll;
            });
        }
    }

    private static toNumberArray(list: string[]) {
        if (list) {
            return list.map((item) => {
                return parseInt(item, 10);
            });
        }
        return [];
    }

    isApplyAllEnabled(columnDataForFilter: ColumnData): boolean {
        // if (columnDataForFilter.query) {
        //     if (columnDataForFilter.dataTypeCode === 'NUMERIC_LIST') {
        //         const list = CrossesAndSelectionsComponent.toNumberArray(columnDataForFilter.query.split(','));
        //         return list.length > 0;
        //     } else {
        //         return true;
        //     }
        // }
        return false;
    }

    onColumnSelectorDeselectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = false;
            }
        });
    }

    onColumnSelectorSelectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = true;
            }
        });
    }

    onColumnSelectorReset() {
        this.onColumnSelectorSelectedAll();
        this.scheduleReload();
    }

    filterSelectOption(isSelectedInFilters: any) {

    }

    protected readonly Math = Math;

    onSampleListChange() {
        this.scheduleTableDataReload(0, this.pageSize);
    }

    isTextFilter(column: ColumnData): boolean {
        // Factors like GID but they should be treated as numeric
        if (column.termId === this.STATIC_COLUMN_GID) {
            return false;
        }

        if (column.dataTypeCode === 'C' || column.dataTypeCode === 'N') {
            return false;
        }

        return true;
    }

    clearFilters() {
        this.displayedColumns.forEach(column => {
            this.resetFilterByColumn(column);
        });
    }

    protected readonly getStaticColumnLabelKey = getStaticColumnLabelKey;
}
